import type { PricedProduct } from '../../../../../services/practiceProductPricing';
import { ruleBreakdown, rulesForVersion } from '../../../../../services/productPricingRules.ts';
import type { DxReportFeeLine } from '../types';

type VersionChoices = Pick<PricedProduct, 'matchingVersions' | 'resolvedVersionId'>;

interface VersionSelection {
    value: string;
    placeholder: string | null;
    needsChoice: boolean;
}

export function isStaleVersion(product: VersionChoices, versionId: string | undefined): boolean {
    return versionId != null && !product.matchingVersions.some((v) => v.id === versionId);
}

// A line without a version bills under the resolved one, so that is shown as
// selected; only a product that resolves to nothing needs staff to choose.
export function versionSelection(product: VersionChoices, versionId?: string): VersionSelection {
    const effectiveId = versionId ?? product.resolvedVersionId;
    if (effectiveId && product.matchingVersions.some((v) => v.id === effectiveId)) {
        return { value: effectiveId, placeholder: null, needsChoice: false };
    }
    if (isStaleVersion(product, versionId)) {
        return {
            value: '',
            placeholder: 'Version no longer available - choose one',
            needsChoice: false
        };
    }
    return { value: '', placeholder: 'Choose version', needsChoice: true };
}

interface VersionPrice {
    listCents: number | null;
    billedCents: number | null;
}

// Falls back to the product-level (resolved version's) prices for a line
// without a version, e.g. one saved before lines recorded it.
function versionPrice(product: PricedProduct, versionId: string | undefined): VersionPrice {
    const version = product.matchingVersions.find((v) => v.id === versionId);
    const listCents = version ? version.priceCents : product.defaultPriceCents;
    const effectiveCents = version ? version.effectivePriceCents : product.effectivePriceCents;
    return { listCents, billedCents: effectiveCents ?? listCents };
}

// The practice's current billed price for the line's version - what reset
// targets. Null when no version is picked or resolves.
export function currentEffectiveCents(product: PricedProduct, versionId?: string): number | null {
    return versionPrice(product, versionId).billedCents;
}

// A stale version's own price is unknown, so any amount kept on it is manual.
export function isOverriddenAmount(
    product: PricedProduct,
    versionId: string | undefined,
    cents: number
): boolean {
    return (
        isStaleVersion(product, versionId) || cents !== currentEffectiveCents(product, versionId)
    );
}

// amountCents is the billed price; list price and discount breakdown are
// frozen for display. No version (ambiguous match) prices the line at 0.
export function feeLineFromProduct(
    product: PricedProduct,
    versionId: string | undefined = product.resolvedVersionId ?? undefined
): DxReportFeeLine {
    const { listCents, billedCents } = versionPrice(product, versionId);
    const applicableRules = versionId ? rulesForVersion(product.rules, versionId) : [];
    const discounts =
        listCents != null && applicableRules.length > 0
            ? ruleBreakdown(listCents, applicableRules).map((step) => ({
                  name: step.rule.name,
                  deltaCents: step.deltaCents
              }))
            : undefined;
    return {
        productId: product.id,
        versionId,
        productName: product.name,
        amountCents: billedCents ?? 0,
        isOverridden: false,
        listPriceCents: listCents ?? undefined,
        discounts
    };
}

// Rebuilt whole so list price and discounts always add up to the amount; a
// stale or missing version falls back to the resolved one.
export function resetFeeLine(product: PricedProduct, line: DxReportFeeLine): DxReportFeeLine {
    return feeLineFromProduct(
        product,
        isStaleVersion(product, line.versionId) ? undefined : line.versionId
    );
}
